import axios from 'axios'

const api = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
})

// Types
export interface Carrier {
  index: number
  frequency_ghz: number | null
  bandwidth_mhz: number | null
}

export interface Radio {
  id: number
  serial_number: string
  hostname: string | null
  sla_profile: string | null
  connected: boolean
  latitude: number | null
  longitude: number | null
  radio_type: 'RN' | 'BN'  // Remote Node or Base Node
  azimuth: number | null  // Antenna azimuth in degrees (0-360)
  frequency_band: string | null  // e.g. "5 GHz" or "6 GHz"
  carrier_count: number | null  // Number of carriers (BN only)
  carriers: Carrier[] | null  // Carrier details (BN only)
  retailer_name: string | null
  market: string | null
  operator: string | null
  primary_bn: string | null  // Assigned base node
  connected_bn: string | null  // Currently connected base node
  needs_attention: boolean  // Connected to wrong BN
  is_rogue: boolean  // Online RN with no Splynx customer (confirmed)
  rssi: number | null  // Signal strength in dBm
  distance: number | null  // Distance to BN in meters
  pathloss: number | null  // Path loss in dB
  alignment_metric?: number | null  // RN antenna alignment metric in dB (RN GUI aiming value)
  // Subscriber Ethernet port (gi1) + attached hosts (RN only)
  eth_port_up?: boolean | null
  eth_port_speed?: string | null
  eth_port_duplex?: string | null
  eth_port_description?: string | null
  eth_in_errors?: number | null
  eth_out_errors?: number | null
  eth_in_discards?: number | null
  eth_in_fcs_errors?: number | null
  eth_errors_1h?: number | null
  host_count?: number | null
  host_macs?: { mac: string; vlan: number }[] | null
  channels?: { id: number; frequency: number; enabled: boolean; width: number; dl_cap_kbps?: number | null; ul_cap_kbps?: number | null }[] | null  // frequency in kHz
  disabled_6ghz_count?: number | null
  // Current RN link capacity (sum across carriers)
  dl_capacity_kbps?: number | null
  ul_capacity_kbps?: number | null
  // Base Node management/capacity fields (BN only)
  mgmt_ip?: string | null
  active_connections?: number | null
  dl_subscriber_rate_kbps?: number | null
  ul_subscriber_rate_kbps?: number | null
  resource_utilization_pct?: number | null
  customer_id: number | null
  customer_name: string | null
  customer_status: string | null  // Splynx account status (active/blocked/disabled/new)
  customer_latitude: number | null   // Splynx service-address pin
  customer_longitude: number | null
  service_name: string | null
  sync_status: 'synced' | 'pending' | 'error' | 'rogue'
  last_synced_at: string | null
  last_seen_at: string | null
}

export interface RadioList {
  items: Radio[]
  total: number
  synced: number
  pending: number
  error: number
  rogue: number
}

export interface ApiQuotaStatus {
  calls_used: number
  calls_limit: number
  calls_remaining: number
  percentage_used: number
  month_year: string
  is_disabled: boolean
  disable_reason: string | null
}

export interface DashboardStats {
  total_radios: number
  connected_radios: number
  disconnected_radios: number
  bn_count: number
  rn_count: number
  needs_attention: number
  synced: number
  unmatched: number
  pending: number
  error: number
  rogue: number
  customers_down: number
  last_sync: string | null
  retailer_filter: string | null
  api_quota?: ApiQuotaStatus
}

export interface CustomerDown {
  serial_number: string
  hostname: string | null
  customer_id: number | null
  customer_name: string | null
  service_name: string | null
  disconnected_since: string | null
  days_offline: number | null
  last_seen_at: string | null
}

export const getCustomersDown = (days?: number) =>
  api.get<{
    items: CustomerDown[]; total: number
    cancelled: CustomerDown[]; cancelled_total: number
    threshold_days: number
  }>(
    '/streaming/customers-down',
    { params: days ? { days } : undefined },
  )

export interface SyncLog {
  id: number
  radio_serial: string
  customer_id: number | null
  action: string
  old_sla: string | null
  new_sla: string | null
  status: 'success' | 'failed'
  error_message: string | null
  triggered_by: string
  created_at: string
}

export interface SyncResult {
  serial_number: string
  success: boolean
  action: string
  old_sla: string | null
  new_sla: string | null
  error: string | null
}

export interface SyncResponse {
  total: number
  success: number
  failed: number
  skipped: number
  dry_run: boolean
  results: SyncResult[]
}

// Auth types
export interface LoginRequest {
  username: string
  password: string
}

export interface LoginResponse {
  access_token: string
  token_type: string
  must_change_password: boolean
}

export interface ChangePasswordRequest {
  current_password: string
  new_password: string
}

export interface ChangePasswordResponse {
  success: boolean
  message: string
  access_token?: string
}

// Auth functions
export const login = (credentials: LoginRequest) =>
  api.post<LoginResponse>('/auth/login', credentials)

export const changePassword = (request: ChangePasswordRequest) =>
  api.post<ChangePasswordResponse>('/auth/change-password', request)

export const getCurrentUser = () =>
  api.get<{ username: string; role: 'viewer' | 'operator' | 'admin'; must_change_password: boolean }>('/auth/me')

// Users & auth flows
export interface AppUser {
  id: number
  username: string
  email: string | null
  full_name: string | null
  role: 'viewer' | 'operator' | 'admin'
  is_active: boolean
  last_login_at: string | null
  pending: boolean
  created_at: string | null
}

export const listUsers = () => api.get<AppUser[]>('/auth/users')
export const inviteUser = (email: string, full_name: string | null, role: string) =>
  api.post<{ id: number; email: string; role: string }>('/auth/users', { email, full_name, role })

// Create a utility/service account directly — no email, admin-set password, active immediately.
export const createUser = (username: string, password: string, role: string, full_name: string | null) =>
  api.post<{ id: number; username: string; role: string }>('/auth/users/create', { username, password, role, full_name })
export const updateUser = (id: number, body: { full_name?: string; role?: string; is_active?: boolean }) =>
  api.patch(`/auth/users/${id}`, body)
export const resetUserPassword = (id: number) => api.post(`/auth/users/${id}/reset`, {})
export const resendInvite = (id: number) => api.post(`/auth/users/${id}/resend`, {})
export const deleteUser = (id: number) => api.delete(`/auth/users/${id}`)
export const forgotPassword = (email: string) => api.post('/auth/forgot', { email })
export const validateAuthToken = (token: string) =>
  api.get<{ valid: boolean; email: string | null; purpose: string | null }>(`/auth/token/${token}`)
export const acceptInvite = (token: string, password: string) =>
  api.post('/auth/accept', { token, password })

export const setAuthToken = (token: string | null) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`
    localStorage.setItem('auth_token', token)
  } else {
    delete api.defaults.headers.common['Authorization']
    localStorage.removeItem('auth_token')
  }
}

export const initAuth = () => {
  const token = localStorage.getItem('auth_token')
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`
  }
}

// API functions
export const getHealth = () => api.get('/health')

// Use streaming endpoints (telemetry data) instead of API calls to preserve quota
export const getDashboardStats = () =>
  api.get<DashboardStats>('/streaming/dashboard/stats')

// A Splynx account that is blocked or disabled — surfaced dark-red in the UI.
export const isCustomerBlocked = (status: string | null | undefined): boolean => {
  const s = (status || '').toLowerCase()
  return s === 'blocked' || s === 'disabled'
}

export const getRadios = (params?: { status?: string; search?: string }) =>
  api.get<RadioList>('/streaming/radios', { params })

export const getMapRadios = () =>
  api.get<Radio[]>('/streaming/map/radios')

export const getRadioDetails = (serial: string) =>
  api.get<Radio>(`/streaming/radios/${serial}`)

export interface HistoryPoint {
  t: string
  dl_capacity_kbps: number | null
  ul_capacity_kbps: number | null
  dl_rate_kbps: number | null
  ul_rate_kbps: number | null
  path_loss: number | null    // dB
  dl_snr: number | null       // dB
  ul_snr: number | null       // dB
  dl_sinr: number | null      // dB
  ul_sinr: number | null      // dB
}

export interface RadioHistory {
  serial_number: string
  hours: number
  bucket_minutes: number
  points: HistoryPoint[]
}

export const getRadioHistory = (serial: string, hours: number) =>
  api.get<RadioHistory>(`/streaming/radios/${serial}/history`, { params: { hours } })

export interface RadioMetrics {
  serial_number: string
  rssi: number | null
  distance: number | null
  pathloss: number | null
}

export interface RefreshMetricsResponse {
  metrics: RadioMetrics[]
}

export const refreshRadioMetrics = (serialNumbers: string[]) =>
  api.post<RefreshMetricsResponse>('/radios/refresh-metrics', {
    serial_numbers: serialNumbers
  })

export interface RefreshCustomersResponse {
  enriched: number
  rogue: number
  errors: number
  total: number
}

export const refreshCustomers = () =>
  api.post<RefreshCustomersResponse>('/radios/refresh-customers', {})

// Splynx customer data for radio expansion view
export interface CustomerData {
  serial_number: string
  customer_found: boolean
  customer_id: number | null
  customer_name: string | null
  customer_latitude: number | null
  customer_longitude: number | null
  service_name: string | null
  tariff_id: number | null
  sla_profile: string | null
  splynx_last_fetched_at: string | null
  cache_hit: boolean
  is_rogue: boolean  // In Splynx but no customer assigned
  error: string | null
}

export const getRadioCustomer = (serial: string, forceRefresh = false) =>
  api.get<CustomerData>(`/radios/${serial}/customer`, {
    params: { force_refresh: forceRefresh }
  })

export interface BnHistoryItem {
  from_bn: string | null
  to_bn: string
  changed_at: string
  from_was_home: boolean
  to_is_home: boolean
  reason: string | null
}

export const getRnBnHistory = (serial: string) =>
  api.get<{ serial_number: string; total: number; items: BnHistoryItem[] }>(
    `/radios/${serial}/bn-history`
  )

export interface SyncPreview {
  serial_number: string
  radio_exists: boolean
  customer_found: boolean
  changes_detected: boolean

  // Current state
  current_hostname: string | null
  current_sla: string | null
  current_latitude: number | null
  current_longitude: number | null

  // Proposed state
  proposed_hostname: string | null
  proposed_sla: string | null
  proposed_latitude: number | null
  proposed_longitude: number | null

  // Customer info
  customer_id: number | null
  customer_name: string | null
  service_name: string | null

  // Error
  error: string | null
}

export interface SyncExecuteResult {
  serial_number: string
  success: boolean
  action: string

  // Changes made
  old_hostname: string | null
  new_hostname: string | null
  old_sla: string | null
  new_sla: string | null
  old_latitude: number | null
  new_latitude: number | null
  old_longitude: number | null
  new_longitude: number | null

  // Metadata
  customer_id: number | null
  customer_name: string | null
  error: string | null
}

export const previewRadioSync = (serial: string) =>
  api.get<SyncPreview>(`/sync/${serial}/preview`)

export const executeRadioSync = (serial: string) =>
  api.post<SyncExecuteResult>(`/sync/${serial}`, { confirmed: true })

export const syncRadio = (serial: string, dryRun = false) =>
  api.post<SyncResponse>(`/sync/${serial}`, { dry_run: dryRun })

export const syncAll = (dryRun = false) =>
  api.post<SyncResponse>('/sync', { dry_run: dryRun })

export const getLogs = (params?: { limit?: number; offset?: number }) =>
  api.get<SyncLog[]>('/logs', { params })

export interface TaranaCredentials {
  api_key?: string
  api_url?: string
}

export interface SplynxCredentials {
  url?: string
  api_key?: string
  api_secret?: string
}

export const testTaranaConnection = (credentials?: TaranaCredentials) =>
  api.post<{ success: boolean; message: string }>('/test/tarana', credentials || {})

export const testSplynxConnection = (credentials?: SplynxCredentials) =>
  api.post<{ success: boolean; message: string }>('/test/splynx', credentials || {})

export interface SaveSettingsRequest {
  tarana_api_key?: string
  tarana_api_url?: string
  tarana_api_enabled?: boolean
  splynx_url?: string
  splynx_api_key?: string
  splynx_api_secret?: string
  sync_frequency?: number
  rogue_policy_sla?: string
  prune_threshold?: number
}

export const saveSettings = (settings: SaveSettingsRequest) =>
  api.post<{ success: boolean; message: string }>('/test/settings', settings)

// Customer-care API keys
export interface ApiKeyInfo {
  id: number
  name: string
  key_prefix: string
  active: boolean
  created_by: string | null
  created_at: string | null
  last_used_at: string | null
}

export const listApiKeys = () => api.get<ApiKeyInfo[]>('/care/keys')

export const createApiKey = (name: string) =>
  api.post<{ id: number; name: string; key_prefix: string; key: string }>('/care/keys', { name })

export const revokeApiKey = (id: number) => api.delete(`/care/keys/${id}`)

export const getSettings = () =>
  api.get<{
    tarana_api_url: string
    tarana_api_key: string
    tarana_api_key_configured: boolean
    tarana_api_enabled: boolean
    splynx_url: string
    splynx_api_key: string
    splynx_api_secret: string
    splynx_api_key_configured: boolean
    splynx_api_secret_configured: boolean
    splynx_configured: boolean
    sync_frequency: number
    rogue_policy_sla: string
    prune_threshold: number
  }>('/test/settings')

export interface BNSyncResult {
  serial_number: string
  success: boolean
  message: string
}

export interface BNSyncResponse {
  total_bns: number
  updated: number
  failed: number
  results: BNSyncResult[]
}

export const syncBNMetadata = () =>
  api.post<BNSyncResponse>('/bn-metadata/refresh', {})

// GPS audit
export interface GpsAuditResult {
  serial_number: string
  band: number | null
  direction: 'to_radio' | 'to_splynx' | null
  customer_id: number | null
  customer_name: string | null
  splynx_lat: number | null
  splynx_lng: number | null
  radio_lat: number | null
  radio_lng: number | null
  geocode_lat: number | null
  geocode_lng: number | null
  geocode_distance_m: number | null
  rf_range_m: number | null
  pin_to_bn_m: number | null
  vision_structure: boolean | null
  vision_description: string | null
  discrepancy_m: number | null
  status: string
  reasons: string[]
  error: string | null
  applied_at: string | null
  updated_at: string | null
}

export interface GpsAuditStatus {
  run_id: string | null
  running: boolean
  dry_run: boolean
  processed: number
  total: number
  tally: Record<string, number>
  error: string | null
}

export const runGpsAudit = (dryRun: boolean) =>
  api.post<{ run_id: string }>('/gps-audit/run', { dry_run: dryRun })

export const getGpsAuditStatus = () =>
  api.get<GpsAuditStatus>('/gps-audit/status')

export const getGpsAuditResults = (statuses: string) =>
  api.get<GpsAuditResult[]>('/gps-audit/results', { params: { status: statuses } })

export const resolveGpsAudit = (serial: string, action: 'apply' | 'dismiss') =>
  api.post(`/gps-audit/${serial}/resolve`, { action })

export const gpsAuditTileUrl = (serial: string) => `/api/v1/gps-audit/${serial}/tile`

export default api
